<template>
  <autocomplete
    ref="autocomplete"
    class="c-facet__search-bar-autocomplete"
    :search="search"
    :placeholder="model.placeholderText"
    :aria-label="model.placeholderText"
    :get-result-value="getResultValue"
    :submit-on-enter="true"
    @submit="submitSearchResult"
  >
    <template
      #default="{
        rootProps,
        inputProps,
        inputListeners,
        resultListProps,
        resultListListeners,
        results,
        resultProps
      }"
    >
      <div
        v-bind="rootProps"
        :class="{
          'flex flex-col h-full oveflow-hidden': isMobile
        }"
      >
        <div>
          <div
            class="flex items-center relative"
            :class="{
              'sticky top-0 border border-outline-grey rounded': isMobile
            }"
          >
            <input
              v-if="placeholder.length"
              ref="input"
              v-bind="inputProps"
              class="flex-grow text-sm autocomplete-input font-light text-outline-grey"
              type="text"
              :class="{
                'has-placeholder': placeholder.length,
                'py-1 pl-2 mr-2': !isMobile,
                'p-2': isMobile
              }"
              :placeholder="placeholder"
              v-on="inputListeners"
              @focus="handleFocus"
              @blur="handleBlur"
            >
            <input
              v-else
              ref="input"
              v-bind="inputProps"
              class="flex-grow text-sm autocomplete-input font-light"
              type="text"
              :class="{
                'autocomplete-input-focused': focused,
                'py-1 pl-2 mr-2': !isMobile,
                'px-3 py-2': isMobile
              }"
              v-on="inputListeners"
              @focus="handleFocus"
              @blur="handleBlur"
            >
            <div
              v-if="isLoading"
              class="absolute ltr:right-0 ltr:mr-3 rtl:left-0 rtl:ml-3 mt-2 "
            >
              <svg viewBox="0 0 50 50" class="new-spinner w-4 h-4">
                <circle
                  class="path"
                  cx="25"
                  cy="25"
                  r="20"
                  fill="none"
                  stroke-width="5"
                />
              </svg>
            </div>
            <button
              v-if="!store.state.tags.length && !store.state.searchTerms.length"
              type="button"
              class="search-btn-facet inline-flex ml-auto w-8 h-8 items-center justify-center mr-1"
              :class="{
                'invisible': isLoading
              }"
              @click="submitSearchResult"
            >
              <span class="sr-only">Search</span>
              <SearchIcon />
            </button>
          </div>
        </div>
        <SelectedFilters
          class="md:hidden mt-4 mb-4"
        />
        <div
          :class="{
            'origin-top-right absolute left-0 w-full w-full max-w-xl bg-white shadow-dropdown mt-4 z-10': !isMobile,
            'flex-1 overflow-auto': isMobile
          }"
          :style="{}"
          v-bind="resultListProps"
          v-on="resultListListeners"
        >
          <div
            v-if="results.length"
            :class="{
              'p-2': !isMobile
            }"
          >
            <template v-if="recentSearchShown && results.length">
              <span
                class="flex text-2xs px-2 py-1 bg-blue-grey"
              >
                <b>{{ model.recentSearchTitle }}</b>
              </span>
              <div
                ref="scrollableContainer"
                class="mt-2"
                :class="{
                  'max-h-260 overflow-auto': !isMobile
                }"
              >
                <div
                  v-for="(result, index) in results"
                  :key="result.id"
                  v-bind="resultProps[index]"
                  class="search-item text-xs cursor-pointer px-2 py-1 hover:bg-blue-grey"
                  :class="{
                    'py-3': isMobile
                  }"
                  v-html="result.name"
                />
              </div>
            </template>
            <template v-else>
              <span
                v-if="results.length && getRecipes(results).length"
                class="flex text-2xs px-2 py-1 bg-blue-grey"
              >
                <b class="mr-1">{{ model.tagsTitle }}</b> ({{ getRecipes(results).length }})
              </span>
              <div
                ref="scrollableContainer"
                class="mt-2"
                :class="{
                  'max-h-260 overflow-auto': !isMobile
                }"
              >
                <div
                  v-for="result in getRecipes(results)"
                  :key="resultProps[result.index].uid"
                  v-bind="resultProps[result.index]"
                  class="search-item text-xs cursor-pointer px-2 py-1 hover:bg-blue-grey last:mb-2"
                  :class="{
                    'py-3': isMobile
                  }"
                  v-html="result.title"
                />
              </div>
              <span
                v-if="results.length && getFacets(results).length"
                class="flex text-2xs px-2 py-1 bg-blue-grey mb-2"
              >
                <b>{{ model.facetTitle }}</b>
              </span>
              <div
                v-for="result in getFacets(results)"
                :key="resultProps[result.index].name"
                v-bind="resultProps[result.index]"
                class="search-item text-xs cursor-pointer px-2 py-1 hover:bg-blue-grey"
                :class="{
                  'py-3': isMobile
                }"
                v-html="result.name"
              />
            </template>
          </div>
        </div>
      </div>
    </template>
  </autocomplete>
</template>

<script>
import Autocomplete from '@trevoreyre/autocomplete-vue'
import SearchIcon from '@/assets/icons/search-icon.svg'
import coreApi from '@/api'
import api from '@/components/facet/api'
import store from '@/components/facet/store'
import pageRefresh from '@/components/facet/page-refresh'
import tracking from '@/helpers/tracking'
import SelectedFilters from '@/components/facet/FacetSelectedFilters.vue'

let cancelToken

export default {
  components: {
    Autocomplete,
    SearchIcon,
    SelectedFilters
  },
  props: {
    model: {
      type: Object,
      required: true
    },
    focusOnShowUp: {
      type: Boolean,
      default: false
    },
    hideTags: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ''
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    isFacetPage: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      value: '',
      store,
      cancelToken: null,
      results: [],
      filteredResults: [],
      tag: null,
      isLoading: false,
      focused: false,
      recentSearchShown: false
    }
  },
  computed: {
    recentSearch() {
      const { recentSearch } = this.store.state
      return recentSearch
    },
    isFacetZero() {
      const { tags, searchTerms } = this.store.state
      return !tags.length && !searchTerms.length
    },
    showRecentSearch() {
      const { isFacetZero, recentSearch } = this
      return isFacetZero && recentSearch.length
    }
  },
  watch: {
    focused(value) {
      this.$emit('focus-changed', value)
    }
  },
  mounted() {
    if (this.focusOnShowUp) {
      this.focusInput()
    }
  },
  methods: {
    async search(query) {
      const { showRecentSearch, recentSearch } = this
      if (query.length === 0) {
        this.recentSearchShown = true
        if (showRecentSearch) {
          return [...recentSearch]
        }
      }
      if (query.trim().length < 2) {
        return []
      }
      this.recentSearchShown = false
      this.isLoading = true

      const { tagUids } = this.store.getters

      if (cancelToken) {
        cancelToken.cancel()
      }

      cancelToken = coreApi.createCancelToken()
      const { tags: currentTags } = this.store.state
      const {
        data: { facets, recipes, tag }
      } = await api.search.get({ query, tagUids, cancelToken })

      const alreadySelectedTag = tag && currentTags.some((currentTag) => currentTag.uid === tag.uid)
      let extendedRecipes
      if (!alreadySelectedTag) {
        extendedRecipes = recipes.map((recipe) => {
          recipe.tag = tag
          return recipe
        })
      } else {
        extendedRecipes = recipes
      }
      const results = [...extendedRecipes, ...facets].map((item, index) => ({ ...item, index }))
      this.filteredResults = [...results]
      this.tag = tag
      this.isLoading = false

      return results
    },
    handleFocus() {
      this.focused = true
    },
    handleBlur() {
      this.focused = false
    },
    focusInput() {
      this.$refs.input.focus()
    },
    getFacets(results) {
      return results.filter(({ uid }) => !uid)
    },
    getRecipes(results) {
      return results.filter(({ uid }) => uid)
    },
    getResultValue() {
      this.value = this.$refs.input.value
      return ''
    },
    async submitSearchResult(result) {
      // It's recipe
      if (result && result.url) {
        document.location.href = result.url
        return
      }

      if (!this.model.isFacetPage) {
        await this.store.dispatch('setIsNotFacetPage')
        await this.store.dispatch('setSortOrder', { sortOrder: 'Relevance' })
      }

      let selectedItem = result
      await this.store.dispatch('showLoader')
      const addItemToStart = this.isMobile
      const firstFoundItem = this.filteredResults[0]

      // is item from recent search
      if (selectedItem && selectedItem.searchTerms) {
        this.selectItemFromRecentHistory(selectedItem)
        return
      }

      // submit the text field and check if found results match with input
      if (!result
        && firstFoundItem && firstFoundItem.name?.toLowerCase() === this.value.toLowerCase()) {
        selectedItem = firstFoundItem
        this.$refs.autocomplete.value = ''
        this.$refs.input.blur()
      }

      if (result == null && this.value.toLowerCase() === this.tag?.name?.toLowerCase()) {
        selectedItem = this.tag
      }

      // it's tag selection
      if (selectedItem && selectedItem.uid) {
        // tracking.selectTagFromAutocomplete(selectedItem.name, this.value)
        await this.store.dispatch('addTag', { tag: selectedItem, addItemToStart })
        tracking.submitRecipeSearch({
          searchTerm: selectedItem.name,
          placement: 'hero filter'
        })
        // tracking.currentTagCombination()
      } else if (selectedItem && selectedItem.tags) {
        // it's facet selection
        await this.store.dispatch('setTags', { tags: selectedItem.tags })
        // tracking.selectFacetFromAutocomplete(selectedItem.name, this.value)
      } else {
        // free text search
        const searchTerm = this.$refs.autocomplete.value
        if (!searchTerm.trim().length) {
          await this.store.dispatch('hideLoader')
          return
        }

        const { currentTagsAndTerms: currentFilters } = this.store.getters
        tracking.submitFilterSearch({
          currentFilters,
          filterName: searchTerm,
          placement: 'hero filter'
        })
        await this.store.dispatch('updateSearchTerms', { searchTerm })

        this.$refs.autocomplete.value = ''
        this.$refs.input.blur()
      }
      await this.store.dispatch('saveRecentSearch')
      await this.store.dispatch('setMobileAutoCompleteVisibility', { value: false })
      await pageRefresh.updatePage()
    },
    selectItemFromRecentHistory(selectedItem) {
      const { searchTerms, tags } = selectedItem
      this.store.dispatch('setTags', { tags })
      this.store.dispatch('setSearchTerms', { searchTerms })
      this.store.dispatch('setMobileAutoCompleteVisibility', { value: false })
      pageRefresh.updatePage()
    },
    async removeSearchTerm(searchTerm) {
      await this.store.dispatch('removeSearchTerm', { searchTerm })
      await pageRefresh.updatePage()
    },
    async removeTag(tag) {
      await this.store.dispatch('removeTag', { tag })
      await pageRefresh.updatePage()
    }
  }
}
</script>

<style scoped>
.search-item[aria-selected="true"] {
  @apply bg-blue-grey;
}
</style>
